import { Component, OnInit, output, input, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { ApButtonComponent } from '@ap/button';
import { ApFormControlPipe } from '@ap/cdk';
import { ApCheckboxComponent } from '@ap/checkbox';
import { ApContentService, ApContentPipe } from '@ap/content';
import { AuthenticationRequest, CryptoService } from '@ap/fido-uaf-frontend-client';
import { TokenTypeId } from '@ap/interface-apic-authenticatie-fido';
import { ErrorMessages } from '@ap/form/errors';
import { KeyValueDisplay, ApKeyComponent, ApKeyValueComponent, ApValueComponent } from '@ap/key-value';
import { markFormControlAsDone } from '@ap/form';
import { ApInputComponent } from '@ap/input';
import { ApPasswordInputComponent } from '@ap/input/password';
import { ApPersistenceService } from '@ap/persistence';

import { ApSubmitComponent } from '../../submit/submit.component';
import { ApTokenService } from '../token.service';
import { ValidationResultType } from '../../validation/validation.model';
import { SignCredentials, SignError, SignResponse, SignResponseForm } from '../../sign.model';

import content from './digicode.content.json';

/**
 * @ngdoc component
 * @name ApDigicodeComponent
 */
@Component({
    selector: 'ap-digicode',
    standalone: true,
    templateUrl: 'digicode.component.html',
    styleUrls: ['./digicode.component.scss'],
    imports: [
        FormsModule,
        ApInputComponent,
        ReactiveFormsModule,
        ApKeyValueComponent,
        ApKeyComponent,
        ApValueComponent,
        ApPasswordInputComponent,
        ApCheckboxComponent,
        ApButtonComponent,
        ApSubmitComponent,
        ApFormControlPipe,
        ApContentPipe
    ]
})
export class ApDigicodeComponent implements OnInit {
    private readonly contentService = inject(ApContentService);
    private readonly tokenService = inject(ApTokenService);
    private readonly persistenceService = inject(ApPersistenceService);

    /**
     * Required - The actionId that identifies the action to be signed
     */
    public readonly actionId = input.required<string>();

    /**
     * Required - Text to show on submit button
     */
    public readonly submitText = input.required<string>();

    /**
     * Required - Authentication request
     */
    public readonly authenticationRequest = input.required<AuthenticationRequest>();

    /**
     * Required - The keyId is used to re-initialize after a failed attempt
     */
    public readonly keyId = input.required<string>();

    /**
     * Text to show on cancel button
     */
    public readonly cancelText = input<string>(undefined);

    /**
     * Is prospect
     * Defaults to false
     */
    public readonly isProspect = input(false);

    /**
     * Id of key to save token by.
     */
    public readonly saveTokenIdKey = input(null);

    /**
     * Id's of elements that describe inputs in digicode.
     */
    public readonly ariaDescribedByIds = input<string[]>([]);

    /**
     * Emits event when user cancels sign process
     */
    public canceled = output<void>();

    /**
     * Emits event when user finishes sign process
     */
    public done = output<void>();

    /**
     * Emits event when signing fails
     */
    public signError = output<SignError>();

    /**
     * Emits event when a retry of signing is triggered
     */
    public retry = output<string>();

    /**
     * Emits event when a restart of the signing process is triggered
     */
    public restart = output<string>();
    public updatePassword = output<SignCredentials>();
    public updateUsernamePassword = output<SignCredentials>();

    public canSign: boolean;

    public signResponse: FormGroup<SignResponseForm>;
    public saveUsername: FormControl<boolean>;
    public keyIdErrorMessages: ErrorMessages;
    public passwordErrorMessages: ErrorMessages;

    protected keyValueDisplay = KeyValueDisplay;

    protected anonymous: boolean;
    protected keyIDs: string[];

    private salt: Int8Array;
    private tokenName: 'digicode' | 'prospect-digicode';

    public async ngOnInit(): Promise<void> {
        this.tokenName = this.isProspect() ? 'prospect-digicode' : 'digicode';
        this.contentService.addContentItems(content);
        const policy = this.authenticationRequest().policy;
        this.anonymous = this.tokenService.isAnonymous(policy);
        this.keyIDs = this.tokenService.getDecodedKeyIds(policy, TokenTypeId.DIGICODE);

        this.salt = await CryptoService.generateSalt();
        this.saveUsername = new FormControl(false);

        this.signResponse = this.createFormGroup();

        this.setErrorMessages();

        if (this.anonymous) {
            this.signResponse.patchValue({
                keyID: this.keyId()
            } as Partial<SignResponse>);
        } else {
            this.signResponse.patchValue({
                keyID: this.keyIDs[0]
            } as Partial<SignResponse>);
        }

        if (this.saveTokenIdEnabled()) {
            this.getUsernameFromPersistence();
        }

        this.canSign = true;
    }

    public cancel(): void {
        // TODO: The 'emit' function requires a mandatory void argument
        this.canceled.emit();
    }

    public getErrorMessage(fieldName: string, errorType: string): string {
        return this.contentService.get(`digicode.${fieldName}.${errorType}`);
    }

    public saveTokenIdEnabled(): boolean {
        return this.anonymous && !!this.saveTokenIdKey();
    }

    public getUsernameFromPersistence() {
        const username = this.persistenceService.getLocal('ap-sign', `${this.saveTokenIdKey()}-${this.tokenName}-username`);
        if (username) {
            this.signResponse.get('keyID').setValue(username);
            this.saveUsername.setValue(true);
        }
    }

    public optionallySaveUsername() {
        if (this.saveUsername.value) {
            return this.persistenceService.setLocal(
                'ap-sign',
                `${this.saveTokenIdKey()}-${this.tokenName}-username`,
                this.signResponse.get('keyID').value
            );
        }
        return this.persistenceService.setLocal('ap-sign', `${this.saveTokenIdKey()}-${this.tokenName}-username`, undefined);
    }

    public sign(): void {
        this.canSign && markFormControlAsDone(this.signResponse);
        if (this.canSign && this.signResponse.valid) {
            this.canSign = false;
            const signResponse = this.getSignResponse();
            const tokenTypeId = this.isProspect() ? TokenTypeId.PROSPECT_DIGICODE : TokenTypeId.DIGICODE;
            this.tokenService
                .validate(this.authenticationRequest(), this.salt, tokenTypeId, signResponse, this.actionId())
                .subscribe((validationResult) => {
                    if (validationResult.resultType === ValidationResultType.DONE) {
                        // TODO: The 'emit' function requires a mandatory void argument
                        this.done.emit();
                        this.optionallySaveUsername();
                    } else {
                        this.signError.emit({
                            key: validationResult.reason,
                            type: 'error'
                        });
                        this.signResponse.controls.response.reset();

                        if (validationResult.resultType === ValidationResultType.PROCESS_RETRY) {
                            this.retry.emit(signResponse.keyID);
                        } else if (validationResult.resultType === ValidationResultType.UPDATE_PASSWORD) {
                            this.updatePassword.emit({
                                username: this.signResponse.value.keyID
                            });
                        } else if (validationResult.resultType === ValidationResultType.UPDATE_USERNAME_PASSWORD) {
                            this.updateUsernamePassword.emit({
                                username: this.signResponse.value.keyID
                            });
                        } else if (validationResult.resultType === ValidationResultType.COMPONENT_RETRY) {
                            this.restart.emit(signResponse.keyID);
                        }
                    }
                });
        }
    }

    private createFormGroup(): FormGroup<SignResponseForm> {
        return new FormGroup<SignResponseForm>({
            keyID: new FormControl<string>(null, [Validators.required]),
            response: new FormControl<string>(null, [Validators.required])
        });
    }

    private setErrorMessages(): void {
        this.keyIdErrorMessages = {
            required: this.contentService.get('digicode.keyID.required')
        };
        this.passwordErrorMessages = {
            required: this.contentService.get('digicode.response.required')
        };
    }

    private getSignResponse(): SignResponse {
        const value = this.signResponse.value as SignResponse;
        if (!value.keyID) {
            value.keyID = this.signResponse.controls.keyID.value;
        }
        value.keyID = value.keyID.trim();
        return value;
    }
}
